import { useState } from "react";
import { Bird, Certificate, Knife } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageCaptionField } from "@/components/ImageCaptionField";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Fresh meat and poultry. Every lot states the method it was slaughtered by and the licence
// the stall trades under, so a buyer can keep to their own custom.
export const FreshMeat = ({ data, mine, busy, act, where, method, setMethod }) => {
  const [lot, setLot] = useState({
    kind: "chicken (desi/country)", breed: "", method: "jhatka (single strike)",
    licence_authority: "", licence_number: "", cuts: ["whole", "curry cut"],
    sell_by: "per kg", price: 0, quantity: 10, state: "fresh", weight_range: "",
    age: "", feed: "not stated", prepared_to_order: true, cold_chain_hours: 6,
    note: "", image_id: "", image_caption: "",
  });
  const [order, setOrder] = useState({});
  const [confirm, setConfirm] = useState({});

  const toggleCut = (c) => setLot({
    ...lot,
    cuts: lot.cuts.includes(c) ? lot.cuts.filter((x) => x !== c) : [...lot.cuts, c],
  });

  const sendProof = async (o) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const form = new FormData();
      form.append("file", file);
      form.append("caption", `Prepared ${o.method}`);
      try {
        const { data: up } = await api.post("/images", form);
        await api.post(`/farm/meat/orders/${o.id}/prepared`, {
          method: confirm[o.id] || o.method, image_id: up.id });
        toast.success("Method confirmed and the photo sent to the buyer");
      } catch (e) { oops(e); }
    };
    input.click();
  };

  return (
    <div className="space-y-4" data-testid="meat-section">
      <div>
        <p className="overline text-slate-500">
          <Bird size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> Fresh meat and poultry
        </p>
        <p className="mt-1 text-sm leading-relaxed text-slate-600" data-testid="meat-explainer">
          Every lot says how it was slaughtered and shows the licence the stall trades under.
          Jhatka severs the head with a single instant strike and is favoured in many Sikh and
          Hindu traditions; halal (zabiha) and kosher (shechita) follow Islamic and Jewish law.
          Filter the board by the method you keep to.
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="meat-method-filter">
          <button type="button" data-testid="meat-method-any" onClick={() => setMethod("")}
            className={`tap-safe border px-2.5 py-1.5 text-xs transition-colors duration-150 ${
              method === ""
                ? "border-[#0F172A] bg-[#0F172A] text-white"
                : "border-border bg-white text-slate-600 hover:border-[#0F172A]"}`}>
            Any method
          </button>
          {(data.methods || []).map((m) => (
            <button key={m} type="button" data-testid={`meat-method-${m.split(" ")[0]}`}
              onClick={() => setMethod(m)}
              className={`tap-safe border px-2.5 py-1.5 text-xs transition-colors duration-150 ${
                method === m
                  ? "border-[#0F172A] bg-[#0F172A] text-white"
                  : "border-border bg-white text-slate-600 hover:border-[#0F172A]"}`}>
              {m}
            </button>
          ))}
        </div>

        {(data.meat || []).length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            Nothing on the meat board{method ? ` done ${method}` : ""} just yet.
          </p>
        ) : (
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {data.meat.map((m) => (
              <div key={m.id} className="border border-border bg-white p-3" data-testid={`meat-${m.id}`}>
                {m.image_url && (
                  <img src={`${BACKEND}${m.image_url}`} alt={m.kind} loading="lazy"
                    className="mb-2 h-32 w-full border border-border object-cover"
                    data-testid={`meat-photo-${m.id}`} />
                )}
                <p className="text-sm font-semibold text-[#0F172A]">
                  {m.kind}{m.breed ? ` · ${m.breed}` : ""}
                </p>
                <p className="inline-flex items-center gap-1 text-xs font-semibold text-[#B3241B]"
                  data-testid={`meat-method-label-${m.id}`}>
                  <Knife size={12} />{m.method}
                </p>
                <p className="text-xs text-slate-500">
                  {m.state}{m.weight_range ? ` · ${m.weight_range}` : ""}
                  {m.age ? ` · ${m.age}` : ""} · {m.feed} · {m.stall_name}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                  {m.symbol}{Number(m.price || 0).toFixed(2)} {m.sell_by} · {m.quantity} left
                </p>
                <p className="text-xs text-slate-500">{(m.cuts || []).join(" · ")}</p>
                <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500"
                  data-testid={`meat-licence-${m.id}`}>
                  <Certificate size={12} />
                  {m.licence_authority || "Licence"} {m.licence_number}
                </p>
                {m.owner_id !== data.me.id && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-1 text-xs text-slate-600">
                        How much
                        <Input data-testid={`meat-qty-${m.id}`} type="number" min={0} step="0.5" className="w-16"
                          value={order[m.id]?.quantity ?? 1}
                          onChange={(e) => setOrder({ ...order, [m.id]: { ...order[m.id], quantity: e.target.value } })} />
                      </label>
                      <label className="flex items-center gap-1 text-xs text-slate-600">
                        My price
                        <Input data-testid={`meat-offer-${m.id}`} type="number" min={0} step="0.01" className="w-20"
                          placeholder={String(m.price)} value={order[m.id]?.offer ?? ""}
                          onChange={(e) => setOrder({ ...order, [m.id]: { ...order[m.id], offer: e.target.value } })} />
                      </label>
                      <select data-testid={`meat-cut-${m.id}`} aria-label="Cut"
                        value={order[m.id]?.cut || (m.cuts || [])[0] || ""}
                        onChange={(e) => setOrder({ ...order, [m.id]: { ...order[m.id], cut: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        {(m.cuts || []).map((c) => <option key={c} value={c}>{String(c)}</option>)}
                      </select>
                      <select data-testid={`meat-how-${m.id}`} aria-label="How"
                        value={order[m.id]?.how || "collection"}
                        onChange={(e) => setOrder({ ...order, [m.id]: { ...order[m.id], how: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        <option value="collection">Collect at the shop</option>
                        {m.state !== "live" && <option value="delivery">Run it round to me</option>}
                      </select>
                      <select data-testid={`meat-pay-${m.id}`} aria-label="Pay"
                        value={order[m.id]?.pay || "online"}
                        onChange={(e) => setOrder({ ...order, [m.id]: { ...order[m.id], pay: e.target.value } })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        <option value="online">Pay online</option>
                        <option value="on_handover">Pay on handover</option>
                      </select>
                    </div>
                    {m.state === "live" && (
                      <p className="text-[11px] text-slate-500" data-testid={`meat-live-note-${m.id}`}>
                        Live — collection only, not sent out on a round.
                      </p>
                    )}
                    {m.prepared_to_order && (
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input type="checkbox" data-testid={`meat-to-order-${m.id}`}
                            checked={!!order[m.id]?.prepare}
                            onChange={(e) => setOrder({ ...order, [m.id]: { ...order[m.id], prepare: e.target.checked } })} />
                          Prepared to order, {m.method}
                        </label>
                        {order[m.id]?.prepare && (
                          <Input data-testid={`meat-prepare-for-${m.id}`} className="w-40"
                            placeholder="For which day?" value={order[m.id]?.prepare_for || ""}
                            onChange={(e) => setOrder({ ...order, [m.id]: { ...order[m.id], prepare_for: e.target.value } })} />
                        )}
                      </div>
                    )}
                    {order[m.id]?.how === "delivery" && (
                      <Input data-testid={`meat-address-${m.id}`} placeholder="Where shall they drop it?"
                        value={order[m.id]?.address || ""}
                        onChange={(e) => setOrder({ ...order, [m.id]: { ...order[m.id], address: e.target.value } })} />
                    )}
                    <Button size="sm" data-testid={`meat-buy-${m.id}`} disabled={busy}
                      onClick={() => act(async () => {
                        const o = order[m.id] || {};
                        const { data: made } = await api.post("/farm/meat/orders", {
                          meat_id: m.id, quantity: Number(o.quantity) || 1,
                          cut: o.cut || (m.cuts || [])[0],
                          prepare_to_order: !!o.prepare, prepare_for: o.prepare_for || "",
                          offer_price: o.offer === "" || o.offer == null ? null : Number(o.offer),
                          how: o.how || "collection", address: o.address || "",
                          lat: where?.lat, lng: where?.lng, pay: o.pay || "online",
                        });
                        setOrder({ ...order, [m.id]: {} });
                        if ((o.pay || "online") === "online" && made.status === "ordered") {
                          const { data: p } = await api.post("/payments/farm/meat/checkout", {
                            order_id: made.id, origin_url: window.location.origin });
                          if (p.url) window.location.href = p.url;
                        }
                      }, "Ordered")}>
                      Buy it
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(data.meat_selling || []).length > 0 && (
        <div data-testid="meat-selling">
          <p className="overline text-slate-500">People buying your meat</p>
          <div className="mt-2 space-y-2">
            {data.meat_selling.map((o) => (
              <div key={o.id} className="border border-border bg-white p-3 text-sm"
                data-testid={`meat-sale-${o.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {o.reference} · {o.name} · {o.quantity} {o.sell_by} {o.kind} ·
                  {" "}{o.symbol}{Number(o.total || 0).toFixed(2)} · {o.status}
                </p>
                <p className="text-xs text-slate-500">
                  {o.cut} · {o.method} · {o.how}
                  {o.prepare_to_order ? ` · prepare for ${o.prepare_for || "the day"}` : ""} ·
                  {" "}{o.pay === "online" ? "paying online" : "paying on handover"}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {o.status === "offered" && (
                    <>
                      <Button size="sm" data-testid={`meat-accept-${o.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/farm/meat/orders/${o.id}/respond`, { accept: true }), "Offer taken")}>
                        Take the offer
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`meat-decline-${o.id}`} disabled={busy}
                        onClick={() => act(() => api.post(`/farm/meat/orders/${o.id}/respond`, { accept: false }), "Turned down")}>
                        No thanks
                      </Button>
                    </>
                  )}
                  {o.status === "ordered" && (
                    <>
                      <select data-testid={`meat-confirm-method-${o.id}`} aria-label="Method done by"
                        value={confirm[o.id] || o.method}
                        onChange={(e) => setConfirm({ ...confirm, [o.id]: e.target.value })}
                        className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                        {(data.methods || []).map((m) => <option key={m} value={m}>{String(m)}</option>)}
                      </select>
                      <Button size="sm" variant="outline" data-testid={`meat-prepared-${o.id}`}
                        onClick={() => sendProof(o)}>
                        Confirm method and send the photo
                      </Button>
                    </>
                  )}
                  {["ordered", "prepared"].includes(o.status) && (
                    <Button size="sm" variant="outline" data-testid={`meat-handover-${o.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/farm/meat/orders/${o.id}/handed-over`), "Handed over")}>
                      Handed over
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.meat_buying || []).length > 0 && (
        <div data-testid="meat-buying">
          <p className="overline text-slate-500">Meat you have bought</p>
          <div className="mt-2 space-y-2">
            {data.meat_buying.map((o) => (
              <div key={o.id} className="border border-border bg-white p-3 text-sm"
                data-testid={`meat-order-${o.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {o.reference} · {o.quantity} {o.sell_by} {o.kind} ({o.cut}) ·
                  {" "}{o.symbol}{Number(o.total || 0).toFixed(2)} · {o.status} · {o.payment_status}
                </p>
                <p className="text-xs text-slate-500">
                  Listed {o.method}
                  {o.method_confirmed ? ` · confirmed done ${o.method_confirmed}` : ""} ·
                  {" "}{o.licence_authority} {o.licence_number}
                </p>
                {o.proof_image_url && (
                  <img src={`${BACKEND}${o.proof_image_url}`} alt="Prepared for your order"
                    loading="lazy" className="mt-2 h-32 w-full border border-border object-cover sm:w-48"
                    data-testid={`meat-proof-${o.id}`} />
                )}
                {o.pay === "online" && o.payment_status === "unpaid"
                  && !["offered", "declined"].includes(o.status) && (
                  <Button className="mt-2" size="sm" data-testid={`meat-pay-now-${o.id}`} disabled={busy}
                    onClick={async () => {
                      try {
                        const { data: p } = await api.post("/payments/farm/meat/checkout", {
                          order_id: o.id, origin_url: window.location.origin });
                        if (p.url) window.location.href = p.url;
                      } catch (e) { oops(e); }
                    }}>
                    Pay now
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {mine && (
        <div className="border border-border bg-white p-4" data-testid="meat-put-up">
          <p className="overline text-slate-500">
            Butchers, poultry and goat farms — put your meat up
          </p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <select data-testid="meat-kind" value={lot.kind} aria-label="Meat"
              onChange={(e) => setLot({ ...lot, kind: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.meat_kinds || []).map((k) => <option key={k} value={k}>{String(k)}</option>)}
            </select>
            <Input data-testid="meat-breed" placeholder="Breed — 'kadaknath', 'boer goat'"
              value={lot.breed} onChange={(e) => setLot({ ...lot, breed: e.target.value })} />
            <select data-testid="meat-method-pick" value={lot.method} aria-label="Slaughter method"
              onChange={(e) => setLot({ ...lot, method: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.methods || []).map((m) => <option key={m} value={m}>{String(m)}</option>)}
            </select>
            <select data-testid="meat-state-pick" value={lot.state} aria-label="State"
              onChange={(e) => setLot({ ...lot, state: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.meat_state || []).map((s) => <option key={s} value={s}>{String(s)}</option>)}
            </select>
            <Input data-testid="meat-licence-authority" placeholder="Licence from — 'FSSAI', 'council'"
              value={lot.licence_authority}
              onChange={(e) => setLot({ ...lot, licence_authority: e.target.value })} />
            <Input data-testid="meat-licence-number" placeholder="Licence number (required)"
              value={lot.licence_number}
              onChange={(e) => setLot({ ...lot, licence_number: e.target.value })} />
            <select data-testid="meat-sell-by" value={lot.sell_by} aria-label="Sold by"
              onChange={(e) => setLot({ ...lot, sell_by: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.sell_by || []).map((s) => <option key={s} value={s}>{String(s)}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Your price
              <Input data-testid="meat-price" type="number" min={0} step="0.01" value={lot.price}
                onChange={(e) => setLot({ ...lot, price: e.target.value })} className="w-24" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              How much you have
              <Input data-testid="meat-quantity" type="number" min={0} value={lot.quantity}
                onChange={(e) => setLot({ ...lot, quantity: e.target.value })} className="w-24" />
            </label>
            <Input data-testid="meat-weight" placeholder="Weight range — '1.2 to 1.6 kg'"
              value={lot.weight_range}
              onChange={(e) => setLot({ ...lot, weight_range: e.target.value })} />
            <Input data-testid="meat-age" placeholder="Age — '6 months'"
              value={lot.age} onChange={(e) => setLot({ ...lot, age: e.target.value })} />
            <select data-testid="meat-feed" value={lot.feed} aria-label="Feed"
              onChange={(e) => setLot({ ...lot, feed: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.feeds || []).map((f) => <option key={f} value={f}>{String(f)}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Out within (hours)
              <Input data-testid="meat-coldchain" type="number" min={0} max={72}
                value={lot.cold_chain_hours}
                onChange={(e) => setLot({ ...lot, cold_chain_hours: e.target.value })} className="w-20" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="meat-to-order-offer" checked={lot.prepared_to_order}
                onChange={(e) => setLot({ ...lot, prepared_to_order: e.target.checked })} />
              I prepare to order on the day
            </label>
            <Input data-testid="meat-note" placeholder="Note — 'dressed this morning'"
              value={lot.note} onChange={(e) => setLot({ ...lot, note: e.target.value })} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {(data.cuts || []).map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid={`meat-cut-offer-${c.replace(/[^a-z]/gi, "-")}`}
                  checked={lot.cuts.includes(c)} onChange={() => toggleCut(c)} />
                {c}
              </label>
            ))}
          </div>
          <div className="mt-3">
            <ImageCaptionField testid="meat-image" label="Photo of the meat — goes on the public board"
              value={lot.image_id} caption={lot.image_caption}
              onChange={({ image_id, image_caption }) => setLot({ ...lot, image_id: image_id || "", image_caption })} />
          </div>
          <Button className="mt-2" size="sm" data-testid="meat-save" disabled={busy}
            onClick={() => act(async () => {
              await api.post("/farm/meat", {
                stall_id: mine.id, kind: lot.kind, breed: lot.breed, method: lot.method,
                licence_authority: lot.licence_authority, licence_number: lot.licence_number,
                cuts: lot.cuts, sell_by: lot.sell_by, price: Number(lot.price) || 0,
                quantity: Number(lot.quantity) || 0, state: lot.state,
                weight_range: lot.weight_range, age: lot.age, feed: lot.feed,
                prepared_to_order: lot.prepared_to_order,
                cold_chain_hours: Number(lot.cold_chain_hours) || 0,
                note: lot.note, image_id: lot.image_id || "",
              });
              setLot({ ...lot, breed: "", note: "", image_id: "", image_caption: "" });
            }, "On the meat board")}>
            Put it up
          </Button>
        </div>
      )}
    </div>
  );
};
